/**
 * The one generic IR → React walker.
 *
 * This is the third consumer of `BlockSpec.toIR`, alongside the XscpData emitter and the MCP
 * HTML emitter. Nobody writes JSX for a block — if a block renders correctly on screen it is
 * structurally correct in the export, because both read the same tree.
 *
 * Roughly 120 lines, and it is the reason preview/export drift is impossible rather than
 * merely discouraged.
 */

import { createElement, type ReactNode } from 'react';
import type { IRElement, IRNode } from '@lp/shared';

export interface RenderOptions {
  /**
   * Fills an `IRSlot`. In the editor this returns Puck's slot component, which renders the
   * child blocks and accepts drops. The exporters resolve the same node by walking the child
   * blocks instead — two resolvers, one tree shape.
   *
   * `className` carries the slot's container classes. Puck's drop zone renders its own
   * wrapper div, so those classes must go ON that wrapper — otherwise the wrapper becomes the
   * layout container's only child and a grid collapses to one cell.
   */
  slot?: (name: string, className?: string) => ReactNode;
  /**
   * Props merged onto the outermost element. Puck needs to attach its own ref and drag
   * handlers to the block's root node.
   */
  rootProps?: Record<string, unknown>;
  /**
   * Resolves an icon name to a glyph.
   *
   * Blocks carry the icon *name* in `data-lp-icon` rather than embedding SVG, so the IR
   * stays serialisable and the exporter can inline the markup at export time. The editor
   * passes a Lucide lookup here; the exporters substitute inline SVG instead.
   */
  icon?: (name: string) => ReactNode;
}

/** Marks an element whose content is an icon glyph rather than IR children. */
export const ICON_ATTR = 'data-lp-icon';

/**
 * HTML attribute → React prop. Only the ones our blocks actually emit.
 *
 * `fetchpriority` matters here: React would warn and drop the lowercase form, which would
 * quietly cost us the LCP hint on hero images.
 */
const PROP_NAMES: Record<string, string> = {
  class: 'className',
  for: 'htmlFor',
  fetchpriority: 'fetchPriority',
  srcset: 'srcSet',
  colspan: 'colSpan',
  rowspan: 'rowSpan',
  maxlength: 'maxLength',
  tabindex: 'tabIndex',
  readonly: 'readOnly',
  autocomplete: 'autoComplete',
};

/** Elements that must not be given children, or React throws. */
const VOID_TAGS = new Set(['img', 'br', 'hr', 'input', 'source', 'track', 'wbr', 'col', 'embed']);

function toReactProps(node: IRElement): Record<string, unknown> {
  const props: Record<string, unknown> = {};

  const className = node.cls.map((c) => c.name).join(' ');
  if (className) props['className'] = className;

  for (const [name, value] of Object.entries(node.attr ?? {})) {
    // Our own bookkeeping, not a real HTML attribute.
    if (name === 'data-wf-asset-id') continue;
    // data-* and aria-* pass through unchanged; React handles them as written.
    if (name.startsWith('data-') || name.startsWith('aria-')) {
      props[name] = value;
      continue;
    }
    props[PROP_NAMES[name] ?? name] = value;
  }

  for (const { name, value } of node.xattr ?? []) props[name] = value;

  return props;
}

function renderNode(node: IRNode, key: string, opts: RenderOptions): ReactNode {
  switch (node.kind) {
    case 'text':
      return node.value;

    case 'slot': {
      const className = node.cls?.map((c) => c.name).join(' ') || undefined;
      return opts.slot ? opts.slot(node.name, className) : null;
    }

    case 'rich':
      return createElement('div', {
        key,
        className: node.cls.map((c) => c.name).join(' ') || undefined,
        dangerouslySetInnerHTML: { __html: node.html },
      });

    case 'embed':
      // innerHTML never executes <script>, so the CTA's click handler stays inert in the
      // editor — which is what we want. It runs on the published Webflow page, where
      // handleConversionClick is actually defined.
      return createElement('div', {
        key,
        className: node.cls.map((c) => c.name).join(' ') || undefined,
        dangerouslySetInnerHTML: { __html: node.html },
      });

    case 'el': {
      const props = toReactProps(node);
      props['key'] = key;

      if (VOID_TAGS.has(node.tag)) return createElement(node.tag, props);

      // An icon element's content is the glyph, not IR children.
      const iconName = node.attr?.[ICON_ATTR];
      if (iconName && opts.icon) return createElement(node.tag, props, opts.icon(iconName));

      const children = node.children.map((child, i) => renderNode(child, `${key}/${i}`, opts));
      return createElement(node.tag, props, ...children);
    }
  }
}

/** Render one IR tree. `rootProps` is merged onto the outermost element. */
export function renderIR(node: IRNode, opts: RenderOptions = {}): ReactNode {
  if (node.kind === 'el' && opts.rootProps) {
    const props = { ...toReactProps(node), ...opts.rootProps, key: 'root' };
    if (VOID_TAGS.has(node.tag)) return createElement(node.tag, props);
    const children = node.children.map((child, i) => renderNode(child, `root/${i}`, opts));
    return createElement(node.tag, props, ...children);
  }
  return renderNode(node, 'root', opts);
}
